import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Server, RefreshCw, Loader2, AlertTriangle, Activity, ShieldCheck, KeyRound, Plug,
  Lock, Users, ClipboardCheck, ShieldAlert, Globe, Plus, Power, Archive, RotateCcw, Trash2,
  HeartPulse, PlayCircle,
} from 'lucide-react';

const STATUS_STYLE = {
  LIVE: 'bg-emerald-500/12 text-emerald-700 border-emerald-500/30',
  DEMO: 'bg-slate-500/12 text-slate-600 border-slate-500/30',
  PENDING_CREDENTIALS: 'bg-amber-500/12 text-amber-700 border-amber-500/30',
  PENDING_APPROVAL: 'bg-sky-500/12 text-sky-700 border-sky-500/30',
  ERROR: 'bg-destructive/12 text-destructive border-destructive/30',
  DISABLED: 'bg-slate-400/12 text-muted-foreground border-slate-400/30',
  DRAFT: 'bg-violet-500/12 text-violet-700 border-violet-500/30',
  ARCHIVED: 'bg-zinc-500/12 text-zinc-600 border-zinc-500/30',
};
const HEALTH_STYLE = { green: 'text-emerald-600', degraded: 'text-amber-600', down: 'text-destructive', unknown: 'text-muted-foreground' };
const ENV_STYLE = {
  demo: 'bg-slate-500/12 text-slate-600 border-slate-500/30',
  staging: 'bg-sky-500/12 text-sky-700 border-sky-500/30',
  production: 'bg-destructive/12 text-destructive border-destructive/30',
};

function hasPerm(perms, mod, act) {
  if (!perms) return false;
  return perms.includes('*:*') || perms.includes(`${mod}:*`) || perms.includes(`${mod}:${act}`);
}

export default function AdminProviders() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [perms, setPerms] = useState([]);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(null); // provider object being managed
  const [preview, setPreview] = useState({ vertical: 'flight', result: null, loading: false });
  const [addOpen, setAddOpen] = useState(false);
  const [adapters, setAdapters] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const canCreds = hasPerm(perms, 'api_credentials', 'manage');
  const canManage = hasPerm(perms, 'providers', 'manage');
  const isSuper = perms.includes('*:*');

  const load = () => {
    setBusy(true);
    api.providersOverview().then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); })
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    document.title = 'Provider Integrations | Admin';
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);
  useEffect(() => {
    if (user) {
      api.rbacMe().then(({ data }) => {
        setPerms(data.permissions || []);
        if ((data.permissions || []).includes('*:*')) {
          api.providerAdapters().then(({ data: a }) => setAdapters(a)).catch(() => {});
        }
      }).catch(() => {});
      load();
    }
  }, [user]);

  const runPreview = () => {
    setPreview((p) => ({ ...p, loading: true, result: null }));
    api.orchestratorPreview(preview.vertical)
      .then(({ data }) => setPreview((p) => ({ ...p, result: data, loading: false })))
      .catch(() => { toast.error('Preview failed'); setPreview((p) => ({ ...p, loading: false })); });
  };

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Provider access required</h1><p className="text-sm text-muted-foreground mt-2">You need the Providers permission to view this page.</p></div></Layout>;

  const env = data?.app_env || 'demo';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-providers">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Server className="h-6 w-6 text-accent" /> Provider integrations</h1>
            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
              Environment <Badge variant="outline" className={`rounded-full uppercase ${ENV_STYLE[env] || ''}`} data-testid="env-indicator">{env}</Badge>
              <span className="inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> secrets are encrypted-at-rest &amp; never displayed</span>
            </div>
          </div>
          <div className="flex gap-2">
            {isSuper && <Button onClick={() => setAddOpen(true)} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-provider"><Plus className="h-4 w-4 mr-1.5" />Add provider</Button>}
            <Button onClick={() => navigate('/admin/employees')} variant="outline" className="rounded-xl" data-testid="link-employees"><Users className="h-4 w-4 mr-1.5" />Employees</Button>
            <Button onClick={() => navigate('/admin/production-readiness')} variant="outline" className="rounded-xl" data-testid="link-readiness"><ClipboardCheck className="h-4 w-4 mr-1.5" />Readiness</Button>
            <Button onClick={load} disabled={busy} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="providers-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />Refresh</Button>
          </div>
        </div>

        {env === 'production' && (
          <div className="mb-5 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2" data-testid="prod-warning">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> You are in <b>PRODUCTION</b>. Credential changes affect live traffic and require explicit confirmation.
          </div>
        )}
        <div className="mb-5 rounded-xl border border-border bg-secondary/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
          Secret backend: <span className="font-mono">{data?.secret_backend || '…'}</span>. Each vertical supports multiple providers simultaneously. A provider only becomes <b>LIVE</b> after an authenticated Test Connection succeeds.
        </div>

        {!data ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <div className="space-y-5" data-testid="providers-list">
            <div className="flex items-center gap-2 justify-end">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="toggle-archived" />
              <span className="text-xs text-muted-foreground">Show archived</span>
            </div>
            {data.verticals.map((v) => {
              const provs = v.providers.filter((p) => showArchived ? true : !p.archived);
              if (provs.length === 0) return null;
              return (
              <Card key={v.vertical} className="p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold capitalize flex items-center gap-2">{v.vertical}
                    <span className="text-xs text-muted-foreground font-normal">{v.enabled_count} enabled · {v.live_count} live</span>
                  </h3>
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {provs.map((p) => (
                    <div key={p.provider_id} className="rounded-xl border border-border p-3 flex flex-col gap-2" data-testid={`provider-${p.provider_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm leading-tight">{p.provider_name}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{p.provider_id}</p>
                        </div>
                        <Badge variant="outline" className={`rounded-full text-[10px] shrink-0 ${STATUS_STYLE[p.status] || ''}`} data-testid={`status-${p.provider_id}`}>{p.status}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Activity className={`h-3.5 w-3.5 ${HEALTH_STYLE[p.health?.state] || 'text-muted-foreground'}`} />
                        <span className={HEALTH_STYLE[p.health?.state] || 'text-muted-foreground'}>{p.health?.state || 'unknown'}</span>
                        <span className="text-muted-foreground">· {p.health?.request_count || 0} req · {p.health?.error_rate || 0}% err</span>
                      </div>
                      {p.required_fields?.length > 0 && (
                        <div className="text-[11px] text-muted-foreground space-y-0.5">
                          {p.credentials.map((c) => (
                            <div key={c.name} className="flex items-center justify-between gap-2">
                              <span className="font-mono truncate">{c.name}</span>
                              <span className={c.configured ? 'text-emerald-600 font-mono' : 'text-amber-600'}>{c.configured ? c.masked : 'not set'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!p.is_mock && (
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-2">
                            <Switch checked={!!p.enabled} disabled={!canManage}
                              onCheckedChange={(val) => {
                                api.providerSetEnabled(p.provider_id, val)
                                  .then(() => { toast.success(`${p.provider_name} ${val ? 'enabled' : 'disabled'}`); load(); })
                                  .catch(() => toast.error('Not permitted'));
                              }}
                              data-testid={`enable-${p.provider_id}`} />
                            <span className="text-xs text-muted-foreground">{p.enabled ? 'Enabled' : 'Disabled'}</span>
                          </div>
                          <Button size="sm" variant="outline" className="rounded-lg h-7 text-xs" onClick={() => setManage(p)} data-testid={`manage-${p.provider_id}`}>
                            <KeyRound className="h-3.5 w-3.5 mr-1" />Manage
                          </Button>
                        </div>
                      )}
                      {p.is_mock && <p className="text-[11px] text-muted-foreground italic">Demo adapter — clearly labelled, never live.</p>}
                    </div>
                  ))}
                </div>
              </Card>
              ); })}
          </div>
        )}

        {/* Orchestrator preview */}
        <Card className="p-5 rounded-2xl mt-6" data-testid="orchestrator-preview">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Plug className="h-4 w-4 text-accent" /> Orchestrator fan-out preview</h3>
          <p className="text-xs text-muted-foreground mb-3">Queries all eligible enabled providers for a vertical through the resilient path (cost guard + circuit breaker). Results are ranked commission-blind.</p>
          <div className="flex items-center gap-2 mb-3">
            <Select value={preview.vertical} onValueChange={(val) => setPreview((p) => ({ ...p, vertical: val }))}>
              <SelectTrigger className="w-40 rounded-lg" data-testid="preview-vertical"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(data?.verticals || []).map((v) => <SelectItem key={v.vertical} value={v.vertical} className="capitalize">{v.vertical}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={runPreview} disabled={preview.loading} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid="run-preview">
              {preview.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run preview'}
            </Button>
          </div>
          {preview.result && (
            <div className="text-sm space-y-2">
              <p className="text-muted-foreground">{preview.result.result_count} result(s) · ranking: <span className="font-mono">{preview.result.ranking}</span></p>
              <div className="flex flex-wrap gap-2">
                {preview.result.providers_queried.map((q) => (
                  <Badge key={q.provider_id} variant="outline" className={`rounded-full ${q.ok ? 'border-emerald-500/30 text-emerald-700' : 'border-destructive/30 text-destructive'}`}>
                    {q.provider_id} · {q.count} · {q.status}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {manage && (
        <ManageDialog provider={manage} appEnv={env} canCreds={canCreds} canManage={canManage} isSuper={isSuper}
          onClose={() => setManage(null)} onChanged={() => { load(); }} onClosed={() => setManage(null)} />
      )}
      {addOpen && (
        <AddProviderDialog adapters={adapters} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />
      )}
    </Layout>
  );
}

function ManageDialog({ provider, appEnv, canCreds, canManage, isSuper, onClose, onChanged, onClosed }) {
  const [credEnv, setCredEnv] = useState(appEnv || 'demo');
  const [fields, setFields] = useState({});
  const [priority, setPriority] = useState(provider.priority ?? 50);
  const [countries, setCountries] = useState((provider.supported_countries || []).join(', '));
  const [searchApproved, setSearchApproved] = useState(!!(provider.capabilities || {}).search_api_approved);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const masked = useMemo(() => Object.fromEntries((provider.credentials || []).map((c) => [c.name, c])), [provider]);

  const lifecycle = (fn, okMsg) => fn(provider.provider_id)
    .then(() => { toast.success(okMsg); onChanged(); onClosed && onClosed(); })
    .catch((e) => toast.error(e?.response?.data?.detail || 'Action failed'));

  const doSaveCreds = (confirm) => {
    const payload = { environment: credEnv, fields, confirm_production: !!confirm };
    setSaving(true);
    api.providerSetCredentials(provider.provider_id, payload)
      .then(() => { toast.success('Credentials saved (encrypted). Run Test Connection to go live.'); setFields({}); onChanged(); })
      .catch((e) => {
        const d = e?.response?.data?.detail || '';
        if (String(d).includes('CONFIRM_PRODUCTION_REQUIRED')) { setConfirmProd(true); }
        else if (e?.response?.status === 403) toast.error('Super admin required for credential changes');
        else toast.error(d || 'Save failed');
      })
      .finally(() => setSaving(false));
  };

  const handleSaveClick = () => {
    if (Object.values(fields).every((v) => !v)) { toast.error('Enter at least one credential value'); return; }
    if (credEnv === 'production') { setConfirmProd(true); return; }
    doSaveCreds(false);
  };

  const doTest = () => {
    setTesting(true);
    api.providerTest(provider.provider_id, credEnv)
      .then(({ data }) => {
        if (data.ok) toast.success(`Test passed → ${data.status}`);
        else toast.warning(`${data.status}: ${data.message}`);
        onChanged();
      })
      .catch((e) => toast.error(e?.response?.status === 403 ? 'Super admin required' : 'Test failed'))
      .finally(() => setTesting(false));
  };

  const saveConfig = () => {
    const body = {
      priority: Number(priority),
      supported_countries: countries.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
      capabilities: { ...(provider.capabilities || {}), search_api_approved: searchApproved },
    };
    api.providerUpdate(provider.provider_id, body)
      .then(() => { toast.success('Config saved'); onChanged(); })
      .catch(() => toast.error('Not permitted'));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-accent" />{provider.provider_name}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{provider.provider_id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Config */}
          {canManage && (
            <div className="rounded-xl border border-border p-3 space-y-3">
              <p className="text-sm font-medium flex items-center gap-1.5"><Globe className="h-4 w-4" />Configuration</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Priority</Label><Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-lg" data-testid="cfg-priority" /></div>
                <div><Label className="text-xs">Countries (CSV)</Label><Input value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="all" className="rounded-lg" data-testid="cfg-countries" /></div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Search API approved</Label>
                <Switch checked={searchApproved} onCheckedChange={setSearchApproved} data-testid="cfg-search-approved" />
              </div>
              <Button size="sm" variant="outline" className="rounded-lg w-full" onClick={saveConfig} data-testid="save-config">Save configuration</Button>
            </div>
          )}

          {/* Credentials */}
          <div className="rounded-xl border border-border p-3 space-y-3">
            <p className="text-sm font-medium flex items-center gap-1.5"><Lock className="h-4 w-4" />Credentials {canCreds ? '' : '(super admin only)'}</p>
            <div>
              <Label className="text-xs">Environment</Label>
              <Select value={credEnv} onValueChange={setCredEnv} disabled={!canCreds}>
                <SelectTrigger className="rounded-lg" data-testid="cred-env"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="demo">Demo</SelectItem>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(provider.required_fields || []).map((name) => (
              <div key={name}>
                <Label className="text-xs font-mono">{name}</Label>
                <Input type="password" autoComplete="new-password" disabled={!canCreds}
                  placeholder={masked[name]?.configured ? masked[name].masked : 'not set'}
                  value={fields[name] || ''} onChange={(e) => setFields((f) => ({ ...f, [name]: e.target.value }))}
                  className="rounded-lg font-mono" data-testid={`cred-${name}`} />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Leave blank to keep the existing value. Stored encrypted; never returned to the browser.</p>
            {canCreds && (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-lg flex-1 bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} onClick={handleSaveClick} data-testid="save-credentials">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save credentials'}
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg" disabled={testing} onClick={doTest} data-testid="test-connection">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test connection'}
                </Button>
              </div>
            )}
            {canCreds && (
              <Button size="sm" variant="ghost" className="rounded-lg w-full text-destructive hover:text-destructive"
                onClick={() => api.providerDeleteCredentials(provider.provider_id, credEnv).then(() => { toast.success('Credentials removed'); onChanged(); onClose(); }).catch(() => toast.error('Failed'))}
                data-testid="delete-credentials">Remove credentials ({credEnv})</Button>
            )}
          </div>

          {/* Health */}
          <div className="rounded-xl border border-border p-3 space-y-1.5" data-testid="health-panel">
            <p className="text-sm font-medium flex items-center gap-1.5"><HeartPulse className="h-4 w-4" />Health &amp; status</p>
            <div className="text-xs text-muted-foreground grid grid-cols-2 gap-1">
              <span>Status: <span className="font-mono">{provider.status}</span></span>
              <span>Circuit: <span className="font-mono">{provider.health?.circuit || 'closed'}</span></span>
              <span>Requests: {provider.health?.request_count || 0}</span>
              <span>Error rate: {provider.health?.error_rate || 0}%</span>
            </div>
            {provider.last_test_message && <p className="text-[11px] text-muted-foreground">Last test: {provider.last_test_message}</p>}
          </div>

          {/* Lifecycle (super admin) */}
          {isSuper && (
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="lifecycle-panel">
              <p className="text-sm font-medium flex items-center gap-1.5"><PlayCircle className="h-4 w-4" />Lifecycle</p>
              <div className="flex flex-wrap gap-2">
                {!provider.enabled
                  ? <Button size="sm" variant="outline" className="rounded-lg text-emerald-600" onClick={() => lifecycle(api.providerActivate, 'Provider activated')} data-testid="activate-provider"><PlayCircle className="h-3.5 w-3.5 mr-1" />Activate</Button>
                  : <Button size="sm" variant="outline" className="rounded-lg text-amber-600" onClick={() => lifecycle(api.providerDeactivate, 'Provider deactivated')} data-testid="deactivate-provider"><Power className="h-3.5 w-3.5 mr-1" />Deactivate</Button>}
                {!provider.archived
                  ? <Button size="sm" variant="outline" className="rounded-lg" onClick={() => lifecycle(api.providerArchive, 'Provider archived')} data-testid="archive-provider"><Archive className="h-3.5 w-3.5 mr-1" />Archive</Button>
                  : <Button size="sm" variant="outline" className="rounded-lg" onClick={() => lifecycle(api.providerRestore, 'Provider restored')} data-testid="restore-provider"><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>}
                {provider.is_custom && (
                  <Button size="sm" variant="ghost" className="rounded-lg text-destructive hover:text-destructive" onClick={() => lifecycle(api.providerDelete, 'Provider deleted')} data-testid="delete-provider"><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">Activation requires a successful authenticated Test Connection — a provider is never auto-marked LIVE.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmProd} onOpenChange={setConfirmProd}>
        <AlertDialogContent data-testid="prod-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="h-5 w-5" />Change PRODUCTION credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to write live production credentials for <b>{provider.provider_name}</b>. This affects real outbound traffic. This action is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { setConfirmProd(false); doSaveCreds(true); }} data-testid="confirm-prod-save">Yes, update production</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}


function AddProviderDialog({ adapters, onClose, onCreated }) {
  const [vertical, setVertical] = useState('flight');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [adapterKey, setAdapterKey] = useState('');
  const [providerType, setProviderType] = useState('api');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [authType, setAuthType] = useState('api_key');
  const [reqFields, setReqFields] = useState('');
  const [countries, setCountries] = useState('');
  const [currencies, setCurrencies] = useState('');
  const [priority, setPriority] = useState(40);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const verticalAdapters = (adapters?.adapters?.[vertical]) || [];
  useEffect(() => { if (verticalAdapters.length && !verticalAdapters.find((a) => a.adapter_key === adapterKey)) setAdapterKey(verticalAdapters[0].adapter_key); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vertical, adapters]);

  const submit = () => {
    if (!name.trim()) { toast.error('Provider name is required'); return; }
    if (!adapterKey) { toast.error('Select an adapter'); return; }
    setSaving(true);
    api.providerCreate({
      vertical, provider_name: name, slug, adapter_key: adapterKey, provider_type: providerType,
      api_base_url: apiBaseUrl || null, auth_type: authType,
      required_fields: reqFields.split(',').map((s) => s.trim()).filter(Boolean),
      supported_countries: countries.split(',').map((s) => s.trim()).filter(Boolean),
      currencies: currencies.split(',').map((s) => s.trim()).filter(Boolean),
      priority: Number(priority), notes,
    }).then(() => { toast.success('Provider created as DRAFT. Configure credentials → Test → Activate.'); onCreated(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Create failed'))
      .finally(() => setSaving(false));
  };

  const selectedAdapter = verticalAdapters.find((a) => a.adapter_key === adapterKey);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-provider-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-accent" />Add provider</DialogTitle>
          <DialogDescription>Registers a provider bound to an allowlisted adapter. It starts as DRAFT — it never goes live until an authenticated Test Connection passes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Vertical</Label>
              <Select value={vertical} onValueChange={setVertical}>
                <SelectTrigger className="rounded-lg capitalize" data-testid="add-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{(adapters?.verticals || []).map((v) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Provider type</Label>
              <Select value={providerType} onValueChange={setProviderType}>
                <SelectTrigger className="rounded-lg" data-testid="add-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(adapters?.provider_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Provider name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Yatra" className="rounded-lg" data-testid="add-name" /></div>
            <div><Label className="text-xs">Slug (optional)</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from name" className="rounded-lg font-mono" data-testid="add-slug" /></div>
          </div>
          <div>
            <Label className="text-xs">Adapter / interface</Label>
            <Select value={adapterKey} onValueChange={setAdapterKey}>
              <SelectTrigger className="rounded-lg" data-testid="add-adapter"><SelectValue placeholder="Select adapter" /></SelectTrigger>
              <SelectContent>{verticalAdapters.map((a) => <SelectItem key={a.adapter_key} value={a.adapter_key}>{a.label}{!a.implemented ? ' · adapter required' : ''}</SelectItem>)}</SelectContent>
            </Select>
            {selectedAdapter && !selectedAdapter.implemented && (
              <p className="text-[11px] text-amber-600 mt-1">This is a generic stub. A dedicated adapter must be implemented before it can be tested/activated — arbitrary URLs are never executed.</p>
            )}
          </div>
          <div><Label className="text-xs">API base URL (validated, SSRF-safe)</Label><Input value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} placeholder="https://api.partner.com" className="rounded-lg font-mono" data-testid="add-baseurl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Auth type</Label>
              <Select value={authType} onValueChange={setAuthType}>
                <SelectTrigger className="rounded-lg" data-testid="add-auth"><SelectValue /></SelectTrigger>
                <SelectContent>{(adapters?.auth_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Priority</Label><Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-lg" data-testid="add-priority" /></div>
          </div>
          <div><Label className="text-xs">Required credential fields (CSV)</Label><Input value={reqFields} onChange={(e) => setReqFields(e.target.value)} placeholder="YATRA_API_KEY, YATRA_SECRET" className="rounded-lg font-mono" data-testid="add-reqfields" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Countries (CSV)</Label><Input value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="all" className="rounded-lg" data-testid="add-countries" /></div>
            <div><Label className="text-xs">Currencies (CSV)</Label><Input value={currencies} onChange={(e) => setCurrencies(e.target.value)} placeholder="INR" className="rounded-lg" data-testid="add-currencies" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg" data-testid="add-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={onClose}>Cancel</Button>
          <Button className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} onClick={submit} data-testid="add-provider-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create provider'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
